import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { salvarFoto } from "../../lib/foto";
import { criarAtividade } from "../queries";
import { AtividadeFoto } from "../ui/AtividadeFoto";
import {
  DEMANDAS_ORDEM,
  DEMANDA_LABEL,
  FATORES_ORDEM,
  FATOR_LABEL,
  INTENSIDADE_LABEL,
  PREVISIBILIDADE_LABEL,
  SISTEMAS_ORDEM,
  SISTEMA_LABEL,
  STATUS_ATIVIDADE_LABEL,
  TIPOS_ORDEM,
  TIPO_LABEL,
  type DemandaRelacional,
  type FatorPsicomotor,
  type IntensidadePsico,
  type Previsibilidade,
  type SistemaSensorioMotor,
  type StatusAtividade,
  type TipoAtividade,
} from "../types";

/**
 * P0.1 — cadastro rico (feito 1×) de uma atividade psicomotora. Só o nome é
 * obrigatório; o resto enriquece a leitura clínica e os derivados (parecidos/insights).
 */
const STATUS_OPCOES: StatusAtividade[] = [
  "nao_usada",
  "em_exploracao",
  "favorita",
  "sensivel",
];

export function NovaAtividade() {
  const nav = useNavigate();
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoAtividade>("jogo");
  const [demanda, setDemanda] = useState<DemandaRelacional | "">("");
  const [fator, setFator] = useState<FatorPsicomotor | "">("");
  const [sistema, setSistema] = useState<SistemaSensorioMotor[]>([]);
  const [intensidade, setIntensidade] = useState<IntensidadePsico | "">("");
  const [previsibilidade, setPrevisibilidade] = useState<Previsibilidade | "">("");
  const [status, setStatus] = useState<StatusAtividade>("nao_usada");
  const [seguranca, setSeguranca] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [midias, setMidias] = useState<string[]>([]);
  const [salvandoMidia, setSalvandoMidia] = useState(false);

  function toggleSistema(s: SistemaSensorioMotor) {
    setSistema((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function anexarBlob(blob: Blob) {
    setSalvandoMidia(true);
    try {
      const ref = await salvarFoto(blob);
      setMidias((cur) => [...cur, ref]);
    } finally {
      setSalvandoMidia(false);
    }
  }

  async function salvar() {
    if (!nome.trim()) return;
    const id = await criarAtividade({
      nome,
      tipo,
      demanda_relacional: demanda || null,
      psicomotor: {
        fator_alvo: fator || null,
        sistema,
        intensidade: intensidade || null,
        previsibilidade: previsibilidade || null,
      },
      status,
      seguranca: seguranca.trim() || null,
      observacoes: observacoes.trim() || null,
      midias,
    });
    nav(`/gingado/atividade/${id}`, { replace: true });
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">nova atividade</h1>
      <p className="tela-sub">só o nome é obrigatório. o resto você completa quando quiser.</p>

      <div className="card">
        <div className="campo">
          <label>nome</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder='ex.: "cama elástica", "estátua / espelho"'
            autoFocus
          />
        </div>

        <div className="campo">
          <label>tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoAtividade)}>
            {TIPOS_ORDEM.map((t) => (
              <option key={t} value={t}>
                {TIPO_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="campo" style={{ margin: 0 }}>
          <label>com quem se faz <span style={{ color: "var(--text-suave)" }}>(opcional)</span></label>
          <select value={demanda} onChange={(e) => setDemanda(e.target.value as DemandaRelacional | "")}>
            <option value="">—</option>
            {DEMANDAS_ORDEM.map((d) => (
              <option key={d} value={d}>
                {DEMANDA_LABEL[d]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">
          mídia <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="row" style={{ marginBottom: "var(--s2)" }}>
            <label className="foto-editavel" title="adicionar foto">
              <AtividadeFoto midias={midias} nome={nome} tipo={tipo} />
              <span className="foto-editavel-cam" aria-hidden>
                📷
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void anexarBlob(f);
                  e.target.value = "";
                }}
              />
            </label>
            <div className="crescer">
              <div className="meta" style={{ color: "var(--text-muted)" }}>
                {midias.length === 0
                  ? "uma foto do material ou um clipe do jogo ajuda o reconhecimento — sobretudo no modo criança."
                  : `${midias.length} ${midias.length === 1 ? "mídia anexada" : "mídias anexadas"}`}
              </div>
            </div>
          </div>

          <label className="btn btn-fantasma btn-bloco" style={{ cursor: "pointer" }}>
            🎬 anexar clipe de vídeo
            <input
              type="file"
              accept="video/*"
              hidden
              disabled={salvandoMidia}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void anexarBlob(f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </section>

      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">perfil psicomotor (opcional)</h2>
        <div className="card">
          <div className="campo">
            <label>fator psicomotor principal (Fonseca)</label>
            <select value={fator} onChange={(e) => setFator(e.target.value as FatorPsicomotor | "")}>
              <option value="">—</option>
              {FATORES_ORDEM.map((f) => (
                <option key={f} value={f}>{FATOR_LABEL[f]}</option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label>sistema sensório-motor (pode marcar mais de um)</label>
            <div className="status-opcoes">
              {SISTEMAS_ORDEM.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`status-opt${sistema.includes(s) ? " sel" : ""}`}
                  aria-pressed={sistema.includes(s)}
                  onClick={() => toggleSistema(s)}
                >
                  {SISTEMA_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          <div className="linha-campos" style={{ marginBottom: 0 }}>
            <div className="campo" style={{ margin: 0 }}>
              <label>intensidade tônica</label>
              <select
                value={intensidade}
                onChange={(e) => setIntensidade(e.target.value as IntensidadePsico | "")}
              >
                <option value="">—</option>
                {(Object.keys(INTENSIDADE_LABEL) as IntensidadePsico[]).map((i) => (
                  <option key={i} value={i}>{INTENSIDADE_LABEL[i]}</option>
                ))}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label>previsibilidade</label>
              <select
                value={previsibilidade}
                onChange={(e) => setPrevisibilidade(e.target.value as Previsibilidade | "")}
              >
                <option value="">—</option>
                {(Object.keys(PREVISIBILIDADE_LABEL) as Previsibilidade[]).map((p) => (
                  <option key={p} value={p}>{PREVISIBILIDADE_LABEL[p]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">segurança física (opcional)</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <textarea
              value={seguranca}
              onChange={(e) => setSeguranca(e.target.value)}
              placeholder='ex.: "supervisão constante na cama elástica; tirar sapatos"'
            />
            <p className="status-regra">
              aparece em destaque antes da atividade e no modo criança. movimento tem
              risco real — o app nunca empurra intensidade.
            </p>
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">situação</h2>
        <div className="card">
          <div className="status-opcoes">
            {STATUS_OPCOES.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-opt${status === s ? " sel" : ""}`}
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
              >
                {STATUS_ATIVIDADE_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">observações (opcional)</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="ex.: 'acalma na transição da escola', 'só com balanço lento'"
            />
          </div>
        </div>
      </section>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={!nome.trim()}
        onClick={salvar}
      >
        salvar atividade
      </button>
    </main>
  );
}
